'use client';

import { atomWithStorage, createJSONStorage } from 'jotai/utils';
import { PrimitiveAtom } from 'jotai';

// ✅ window 존재 여부를 확인해서 SSR 시 안전하게 동작하도록 처리
const isClient = typeof window !== 'undefined';

// SSR-safe storage helper - 서버에서는 no-op storage를 제공
const createDummyStorage = (): Storage => ({
  length: 0,
  clear: () => {
    // no-op
  },
  getItem: () => null,
  key: () => null,
  removeItem: () => {
    // no-op
  },
  setItem: () => {
    // no-op
  },
});

const getLocalStorage = () => {
  return isClient ? localStorage : createDummyStorage();
};

const getSessionStorage = () => {
  return isClient ? sessionStorage : createDummyStorage();
};

// LocalStorage용 storage
const localStorageHelper = createJSONStorage<any>(getLocalStorage);

// SessionStorage용 storage
const sessionStorageHelper = createJSONStorage<any>(getSessionStorage);

// LocalStorage를 사용하는 atom 생성 헬퍼
export function atomWithLocalStorage<T>(
  key: string,
  initialValue: T,
  getOnInit = true,
): PrimitiveAtom<T> {
  return atomWithStorage(key, initialValue, localStorageHelper, {
    getOnInit,
  });
}

/**
 * 쓰기 실패를 삼키는 LocalStorage — 용량 초과(QuotaExceededError)나 저장 차단 환경에서
 * setItem 이 던져 호출부 로직까지 깨지는 것을 막는다. 영속이 부가 기능인 값에만 쓴다.
 */
const safeLocalStorageHelper = {
  ...localStorageHelper,
  setItem: (key: string, value: any) => {
    try {
      localStorageHelper.setItem(key, value);
    } catch {
      // 저장만 포기하고 메모리 상태는 유지한다.
    }
  },
  removeItem: (key: string) => {
    try {
      localStorageHelper.removeItem(key);
    } catch {
      // no-op
    }
  },
};

/**
 * LocalStorage atom 생성 헬퍼 (쓰기 실패 무시 버전).
 * 저장에 실패해도 이번 세션의 상태는 그대로 살아야 하는 값에 쓴다.
 */
export function atomWithSafeLocalStorage<T>(
  key: string,
  initialValue: T,
  getOnInit = true,
): PrimitiveAtom<T> {
  return atomWithStorage(key, initialValue, safeLocalStorageHelper, {
    getOnInit,
  });
}

// SessionStorage를 사용하는 atom 생성 헬퍼
export function atomWithSessionStorage<T>(
  key: string,
  initialValue: T,
): PrimitiveAtom<T> {
  return atomWithStorage(key, initialValue, sessionStorageHelper, {
    getOnInit: true,
  });
}
